import { Observable, of } from "rxjs";

    /**
     * @description
     *  Clase de soporte para manipular el sistema de tipos, o realizar
     *  acciones donde este carece de funcionalidad.
     */

export class TypeSystem {

    /**
     * @description
     *  Objeto completamente vacío, incluso sin prototipo.
     */
    public static readonly EmptyObject: {} = Object.create(null);

    private constructor() {
        throw new Error(
            "Nunca debe instanciar o heredar de esta clase (es de soporte).");
    }

	/**
	 * @description
	 *	Inicia una cadena de operaciones asincrónicas.
	 *
	 * @returns
	 *	Un observable del objeto vacío (i.e., TypeSystem.EmptyObject).
	 */
	public static async(): Observable<{}> {
		return TypeSystem.asyncFrom(this.EmptyObject);
	}

	/**
	 * @description
	 *	Inicia una cadena de operaciones asincrónicas con un valor inicial.
	 *
	 * @returns
	 *	Un observable del objeto inicial.
	 */
	public static asyncFrom<T>(value: T): Observable<T> {
		return of(value);
	}

	/**
	 * @description
	 *	Extrae la primera línea de una cadena de texto.
	 */
	public static firstLine(value: string): string {
		return value.replace(/\r?\n.+/g, "");
	}

    /**
     * @description
     *  Computa un vector aplanado recursivamente, lo que implica recorrer
     *  sistemáticamente el parámetro de entrada y agregar cada elemento
     *  hallado a un nuevo vector, en el mismo orden en que se encontró,
     *  realizando una búsqueda en profundidad.
     *
     *  Este método no elimina elementos bajo ninguna regla específica, con lo
     *  cual, el vector de salida poseerá elementos 'null' y 'undefined' si
     *  los mismos se encontraban en el vector de entrada.
     *
     * @example
     *  Para el siguiente vector:
     *
     *      ["x", 123, [],
     *          [null, [[]], undefined, [0.4, [new Array()]]],
     *          (x, y) => x + y
     *      ]
     *
     *  este método producirá la siguiente salida:
     *
     *      ["x", 123, null, undefined, 0.4, (x, y) => x + y]
     *
     *  Nótese que los vectores vacíos no aparecen en la salida, ya que no
     *  aportan elementos adicionales una vez explorados.
     *
     * @param array
     *  El vector de entrada a aplanar, posiblemente multidimensional.
     *
     * @returns
     *  Un nuevo vector unidimensional con todos los elementos hallados en el
     *  vector de entrada. Si la entrada no era un vector, entonces se
     *  devuelve un vector que posee este único elemento.
     */
    public static flattenize<T>(array: T[] = []): Array<T> {
        if (Array.isArray(array)) {
            const flattenized: Array<T> = new Array<T>();
            for (let i = 0; i < array.length; ++i) {
                const element = array[i];
                if (Array.isArray(element)) {
                    flattenized.push(...this.flattenize(element));
                }
                else {
                    flattenized.push(element);
                }
            }
            return flattenized;
        }
        else {
            return [array];
        }
    }

	/**
	 * @description
	 *	Permite transformar opcionalmente un valor en caso de que el mismo sea
	 *	nulo o indefinido, lo que permite anidar llamadas a métodos sobre el
	 *	valor original de forma segura.
	 *
	 * @param value
	 *	El valor original, posiblemente nulo o indefinido.
	 * @param defaultValue
	 *	El valor por defecto.
	 *
	 * @returns
	 *	El valor por defecto, o el original si no era 'null' o 'undefined'.
	 */
	public static orElse<T>(value: T, defaultValue: T): T {
		if (value !== null && value !== undefined) {
			return value;
		}
		else {
			return defaultValue;
		}
	}

	/**
	 * @description
	 *	Transforma una cadena en un número. La cadena de entrada puede ser
	 *	'null' o 'undefined', en cuyo caso se transformará en 0.0. Si la
	 *	cadena de entrada posee comas, estas se traducirán en puntos y luego
	 *	se aplicará la conversión.
	 *
	 * @param value
	 *	Una cadena que representa el número deseado.
	 *
	 * @returns
	 *	El número obtenido, posiblemente 'NaN' si estaba malformado y no se
	 *	pudo reparar.
	 */
	public static toNumber(value: string): number {
		const valueWithDots = TypeSystem.orElse(value, "0.0").replace(/,/g, ".");
		const lastDot = valueWithDots.lastIndexOf(".");
		const prefix = valueWithDots.substring(0, lastDot).replace(/\./g, "");
		const sufix = valueWithDots.substring(lastDot + 1, valueWithDots.length);
		return +(`${prefix === "" ? prefix : `${prefix}.` }${sufix}`);
	}
}
